import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { BuildingManagementService } from 'src/app/building-managment/building-management.service';
import { Building, IBuilding } from 'src/app/building-managment/building-management-interfaces';
import { NotFoundError } from 'src/app/shared/errors/errors';
import { NotificationService } from 'src/app/shared/errors/notification.service';
import { hasRequiredContact, PopupProfile } from '../profile-management.service';

export interface AssignUserTypeOption {
  value: number;
  labelKey: string;
  descriptionKey: string;
  contactRequired: boolean;
}

export const ASSIGN_USER_TYPE_OPTIONS: AssignUserTypeOption[] = [
  { value: 1, labelKey: 'PROPERTY_MANAGER_ADMIN', descriptionKey: 'DESCRIPTION_PROPERTY_MANAGER_ADMIN', contactRequired: true },
  { value: 2, labelKey: 'PROPERTY_MANAGER_USER', descriptionKey: 'DESCRIPTION_PROPERTY_MANAGER_USER', contactRequired: true },
  { value: 3, labelKey: 'INSTALLER_ADMIN', descriptionKey: 'DESCRIPTION_INSTALLER_ADMIN', contactRequired: true },
  { value: 4, labelKey: 'INSTALLER_USER', descriptionKey: 'DESCRIPTION_INSTALLER_USER', contactRequired: false },
  { value: 5, labelKey: 'RESIDENT_ADMIN', descriptionKey: 'DESCRIPTION_RESIDENT_ADMIN', contactRequired: false },
  { value: 6, labelKey: 'RESIDENT', descriptionKey: 'DESCRIPTION_RESIDENT', contactRequired: false },
];

// PMA/PMU receive site-wide access, so building/unit/time selection is skipped.
export function isSitewideUserType(type?: number): boolean {
  return type === 1 || type === 2;
}

export function isContactRequiredUserType(type?: number): boolean {
  return ASSIGN_USER_TYPE_OPTIONS.some(option => option.value === type && option.contactRequired);
}

export const ASSIGN_STEP = {
  userType: 19, building: 20, timeEnd: 23, confirm: 24, editInformation: 25,
  accessAssigned: 26, completeUserInfo: 27, inviteSent: 28,
};

@Component({
  selector: 'app-profile-assign-user-type',
  templateUrl: './profile-assign-user-type.component.html',
  styleUrls: ['./profile-assign-user-type.component.scss'],
})
export class ProfileAssignUserTypeComponent implements OnDestroy {
  @Input() dialogRef?: { close: () => void };
  @Input() siteId!: string;
  @Input() profileDetails?: PopupProfile;
  @Output() buildingsLoaded = new EventEmitter<IBuilding[]>();
  isLoading = false;
  private destroyed = new Subject<void>();
  @Input() stepper = 19;
  @Output() stepperChange = new EventEmitter<number>();
  @Input() selectedType?: number;
  @Output() selectedTypeChange = new EventEmitter<number>();
  // Set after a merge so the existing user's role cannot be changed here.
  @Input() allowedTypes?: number[];

  get options(): AssignUserTypeOption[] {
    const allowed = this.allowedTypes;
    return allowed ? ASSIGN_USER_TYPE_OPTIONS.filter(option => allowed.includes(option.value)) : ASSIGN_USER_TYPE_OPTIONS;
  }

  constructor(
    private buildingService: BuildingManagementService,
    private notificationService: NotificationService
  ) {}

  select(option: AssignUserTypeOption): void {
    this.selectedTypeChange.emit((this.selectedType = option.value));
  }

  continue(): void {
    const option = this.options.find(o => o.value === this.selectedType);
    if (!option || this.isLoading) return;
    const nextStep = option.contactRequired && !hasRequiredContact(this.profileDetails)
      ? ASSIGN_STEP.completeUserInfo
      : ASSIGN_STEP.building;
    if (isSitewideUserType(option.value)) {
      this.stepperChange.emit(nextStep === ASSIGN_STEP.building ? ASSIGN_STEP.confirm : nextStep);
      return;
    }
    this.isLoading = true;
    this.buildingService.getAllBuildingsBySiteId(this.siteId)
      .pipe(takeUntil(this.destroyed), finalize(() => this.isLoading = false))
      .subscribe({
        next: (buildings: Building) => this.openBuildings(buildings || [], nextStep),
        error: (error: any) => {
          // A site without buildings still opens the popup to show its empty state.
          if (error instanceof NotFoundError) this.openBuildings([], nextStep);
          else this.notificationService.showError(error?.message);
        },
      });
  }

  private openBuildings(buildings: IBuilding[], nextStep: number): void {
    const sorted = [...buildings].sort((a, b) =>
      (a.buildingName || '').localeCompare(b.buildingName || '', undefined, { numeric: true }));
    this.buildingsLoaded.emit(sorted);
    this.stepperChange.emit(nextStep);
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  back(): void {
    if (!this.isLoading) this.stepperChange.emit(1);
  }

  close(): void {
    if (!this.isLoading) this.dialogRef?.close();
  }
}
